import api from "@/lib/api";

function getBusinessIdFromStorage() {
  try {
    return window?.localStorage?.getItem("current_business_id") || null;
  } catch {
    return null;
  }
}

function headersForBusiness(businessId) {
  const bid = businessId ?? getBusinessIdFromStorage();
  return bid ? { "X-Business-Id": String(bid) } : {};
}

function toFormData({ body = "", files = [] } = {}) {
  const fd = new FormData();
  fd.append("body", body ?? "");

  const arr = Array.isArray(files) ? files : files ? [files] : [];
  arr.forEach((f) => {
    if (!f) return;
    // trimitem în ambele chei, ca să fie compatibil cu orice backend/parser
    fd.append("files", f);
    // fd.append("files[]", f); // dacă vrei extra compat, poți activa și asta
  });

  return fd;
}


/**
 * IMPORTANT:
 * Backend-ul tău validează appointment/thread în contextul X-Business-Id.
 * Deci trebuie să trimiți businessId-ul corect (al programării) la aceste endpoint-uri.
 *
 * Toate funcțiile acceptă param opțional: { businessId }.
 */

// ---------- STAFF ----------
export const getStaffThread = (appointmentId, { businessId } = {}) =>
  api.get(`/b/chat/thread/${appointmentId}`, { headers: headersForBusiness(businessId) });

export const getStaffMessages = (
  appointmentId,
  { limit = 50, before, after } = {},
  { businessId } = {}
) =>
  api.get(`/b/chat/messages/${appointmentId}`, {
    params: { limit, before, after },
    headers: headersForBusiness(businessId),
  });

/**
 * postStaffMessage
 * Acceptă:
 *  - postStaffMessage(id, "text", {businessId})
 *  - postStaffMessage(id, {body:"text"}, {businessId})
 *  - postStaffMessage(id, {body:"text"}, {businessId, files:[File,...]})
 */
export const postStaffMessage = (appointmentId, body, { businessId, files } = {}) => {
  const payloadObj =
    typeof body === "object" && body !== null ? body : { body: String(body ?? "") };

  const hasFiles = Array.isArray(files) ? files.length > 0 : !!files;

  if (hasFiles) {
    const fd = toFormData({ body: payloadObj.body ?? "", files });
    return api.post(`/b/chat/messages/${appointmentId}`, fd, {
      headers: {
        ...headersForBusiness(businessId),
        // important: axios va seta boundary-ul corect dacă NU setăm noi manual
      },
    });
  }

  return api.post(`/b/chat/messages/${appointmentId}`, payloadObj, {
    headers: headersForBusiness(businessId),
  });
};

export const markStaffRead = (appointmentId, { businessId } = {}) =>
  api.post(`/b/chat/read/${appointmentId}`, {}, { headers: headersForBusiness(businessId) });

/**
 * invite link
 *  - sendSms: true  -> ?send_sms=1
 *  - rotate: true   -> ?rotate=1 (generează token nou)
 */
export const createChatInvite = (
  appointmentId,
  { sendSms = false, rotate = false } = {},
  { businessId } = {}
) => {
  const qs = new URLSearchParams();
  if (sendSms) qs.set("send_sms", "1");
  if (rotate) qs.set("rotate", "1");
  const q = qs.toString();

  return api.post(`/b/chat/invite/${appointmentId}${q ? `?${q}` : ""}`, {}, {
    headers: headersForBusiness(businessId),
  });
};

/**
 * Download attachment (STAFF)
 * Endpoint: GET /api/b/chat/attachment/<id>/
 * Necesită X-Business-Id, returnează image/webp
 */
export const getStaffAttachmentBlob = async (attachmentId, { businessId } = {}) => {
  const res = await api.get(`/b/chat/attachment/${attachmentId}`, {
    headers: headersForBusiness(businessId),
    responseType: "blob",
  });
  return res?.data ?? res;
};

// ---------- PUBLIC ----------
export const getPublicThread = (token) => api.get(`/public/chat/thread/${token}`);

export const getPublicMessages = (token, { limit = 50, before, after } = {}) =>
  api.get(`/public/chat/messages/${token}`, { params: { limit, before, after } });

/**
 * postPublicMessage
 * Acceptă:
 *  - postPublicMessage(token, "text")
 *  - postPublicMessage(token, {body:"text"}, {files:[...]})
 */
export const postPublicMessage = (token, body, { files } = {}) => {
  const payloadObj =
    typeof body === "object" && body !== null ? body : { body: String(body ?? "") };

  const hasFiles = Array.isArray(files) ? files.length > 0 : !!files;

  if (hasFiles) {
    const fd = toFormData({ body: payloadObj.body ?? "", files });
    return api.post(`/public/chat/messages/${token}`, fd);
  }

  return api.post(`/public/chat/messages/${token}`, payloadObj);
};

export const markPublicRead = (token) => api.post(`/public/chat/read/${token}`, {});

/**
 * Public attachment URL helper:
 * Endpoint: GET /api/public/chat/attachment/<token>/<attachment_id>/
 * (nu necesită headers)
 */
export const getPublicAttachmentUrl = (token, attachmentId) =>
  `/api/public/chat/attachment/${token}/${attachmentId}`;
